/**
 * Per-user notification preferences, stored in localStorage keyed by userID.
 *
 * Why localStorage instead of the site-wide settings table:
 *   - The current Vuex/settings flow has a long-standing bug where
 *     `state.customer.allSettings` is never set (only `state.customer.settings`
 *     is). Components that mapState the wrong path read undefined and
 *     `isTaskNotificationEnabled(undefined)` defaults true, masking saves.
 *   - These prefs are inherently per-person (Tanya's clinicians want SMS off
 *     during chair time; front desk wants SMS on). Per-user is the right shape.
 *   - localStorage is per-browser-per-user automatically — no employeeID
 *     keying needed in any DB schema, no /save-settings round trip on every
 *     toggle, no DELETE-LIKE prefix collision risk against A2P data.
 *
 * Defaults are PER-KEY (not per-object) so a partial stored object fills
 * missing keys with their individual defaults. Don't change this — see test
 * "partial stored object fills missing keys with defaults".
 *
 * Keying: `teamio_notif_prefs_${userID}` so two users sharing one workstation
 * (front desk pass-the-keyboard) don't trample each other's prefs.
 */

export const STORAGE_KEY_PREFIX = 'teamio_notif_prefs_';

export const DEFAULTS = Object.freeze({
  chat:   { soundPush: true,  sound: 'elevator-ding.mp3' },
  sms:    { soundPush: false, sound: 'simple-notification-152054.mp3' },
  kanban: { soundPush: true,  sound: 'bell-123742.mp3' },
  task:   { soundPush: true,  sound: 'bell-123742.mp3' },
});

const KNOWN_CATEGORIES = Object.keys(DEFAULTS);

function storageKey(userID) {
  return STORAGE_KEY_PREFIX + userID;
}

function readRaw(userID) {
  if (!userID) return null;
  try {
    const raw = localStorage.getItem(storageKey(userID));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    // Corrupt JSON — pretend it isn't there. Caller falls back to defaults.
    return null;
  }
}

function writeRaw(userID, obj) {
  if (!userID) return;  // No-op for unauthenticated callers.
  try {
    localStorage.setItem(storageKey(userID), JSON.stringify(obj));
  } catch (_) {
    // localStorage can throw on quota — silently swallow; defaults will apply.
  }
}

export function getPrefs(userID) {
  const stored = readRaw(userID) || {};
  const out = {};
  for (const cat of KNOWN_CATEGORIES) {
    const def = DEFAULTS[cat];
    const got = (stored[cat] && typeof stored[cat] === 'object') ? stored[cat] : {};
    out[cat] = {
      soundPush: typeof got.soundPush === 'boolean' ? got.soundPush : def.soundPush,
      sound: typeof got.sound === 'string' && got.sound.length > 0 ? got.sound : def.sound,
    };
  }
  return out;
}

export function setPref(userID, category, partial) {
  if (!userID) return;
  if (!KNOWN_CATEGORIES.includes(category)) return;
  const current = getPrefs(userID);
  current[category] = { ...current[category], ...(partial || {}) };
  writeRaw(userID, current);
}

export function isEnabled(userID, category) {
  if (!KNOWN_CATEGORIES.includes(category)) return true;  // Unknown → don't suppress.
  return getPrefs(userID)[category].soundPush;
}

export function getSound(userID, category) {
  if (!KNOWN_CATEGORIES.includes(category)) return DEFAULTS.task.sound;
  return getPrefs(userID)[category].sound;
}

export function resetForTesting() {
  // no-op for now
}
